import { createInvalidResponseError, FormbrewError } from "./errors.ts";
import { isErrorEnvelope } from "./guards.ts";
import { isAbortError } from "./predicates.ts";

export const performRequest = async (
  fetchImplementation: typeof globalThis.fetch,
  url: string,
  init: RequestInit,
): Promise<Response> => {
  try {
    return await fetchImplementation(url, init);
  } catch (error) {
    if (init.signal?.aborted) {
      throw init.signal.reason ?? error;
    }

    if (isAbortError(error)) {
      throw error;
    }

    throw new FormbrewError("Unable to reach Formbrew.", {
      cause: error,
      code: "NetworkError",
    });
  }
};

export const readResponsePayload = async (
  response: Response,
  signal?: AbortSignal,
): Promise<unknown> => {
  try {
    return await response.json();
  } catch (error) {
    if (signal?.aborted) {
      throw signal.reason ?? error;
    }

    if (isAbortError(error)) {
      throw error;
    }

    if (!response.ok) {
      return undefined;
    }

    throw createInvalidResponseError(response.status);
  }
};

export const assertSuccessfulResponse = (response: Response, payload: unknown): void => {
  if (response.ok) {
    return;
  }

  if (isErrorEnvelope(payload)) {
    throw new FormbrewError(payload.message, {
      code: payload.error,
      details: payload.details,
      status: response.status,
    });
  }

  throw new FormbrewError(`Formbrew request failed with status ${response.status}.`, {
    code: "HttpError",
    status: response.status,
  });
};

export const createRequestHeaders = (origin: string | undefined, includeJson = false): Headers => {
  const headers = new Headers();

  if (includeJson) {
    headers.set("Content-Type", "application/json");
  }

  if (origin) {
    headers.set("Origin", origin);
  }

  return headers;
};
